import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { API_ROUTES } from '@/constants/apiRoutes';
import { ProgramSettings, ProgramSettingsFormValues } from '@/types/setting';
import { api, formatCurrencyForPost } from '@/utils/api';
import { formatStringToNumber } from '@/utils/string';

export const useFetchProgramSettings = () => {
  return useQuery({
    queryFn: async () => {
      const { data } = await api<ProgramSettings>({
        url: `${API_ROUTES.PROGRAM_SETTINGS}`,
        version: 2,
        method: 'get',
      });

      return data;
    },
    queryKey: [API_ROUTES.PROGRAM_SETTINGS],
  });
};

export const useProgramSettings = () => {
  const queryClient = useQueryClient();
  const {
    mutateAsync: updateProgramSettings,
    isPending: isLoadingUpdateProgramSettings,
  } = useMutation({
    mutationFn: async (data: ProgramSettingsFormValues) => {
      const { status } = await api({
        url: `${API_ROUTES.PROGRAM_SETTINGS}`,
        version: 2,
        method: 'patch',
        data: {
          index_fact_sheet_link: data.indexFactSheetLink || null,
          rate_saving_interest: formatStringToNumber(data.rateSavingInterest),
          minimum_init_deposit: formatCurrencyForPost(data.minimumInitDeposit),
          maximum_init_deposit: formatCurrencyForPost(data.maximumInitDeposit),
          fee_incoming_domestic: formatCurrencyForPost(
            data.feeIncomingDomestic,
          ),
          fee_outcome_domestic: formatCurrencyForPost(data.feeOutcomeDomestic),
          fee_monthly_saving_account: formatCurrencyForPost(
            data.feeMonthlySavingAccount,
          ),
          ach_credit_program_limit_per_rolling_day: formatCurrencyForPost(
            data.achCreditProgramLimitPerRollingDay,
          ),
          ach_debit_program_limit_per_rolling_day: formatCurrencyForPost(
            data.achDebitProgramLimitPerRollingDay,
          ),
          ach_credit_account_limit_per_calendar_day: formatCurrencyForPost(
            data.achCreditAccountLimitPerCalendarDay,
          ),
          ach_debit_account_limit_per_calendar_day: formatCurrencyForPost(
            data.achDebitAccountLimitPerCalendarDay,
          ),
          ach_credit_limit_per_transaction: formatCurrencyForPost(
            data.achCreditLimitPerTransaction,
          ),
          ach_debit_limit_per_transaction: formatCurrencyForPost(
            data.achDebitLimitPerTransaction,
          ),
          wire_out_program_limit_per_rolling_day: formatCurrencyForPost(
            data.wireOutProgramLimitPerRollingDay,
          ),
          wire_out_account_limit_per_calendar_day: formatCurrencyForPost(
            data.wireOutAccountLimitPerCalendarDay,
          ),
          wire_out_limit_per_transaction: formatCurrencyForPost(
            data.wireOutLimitPerTransaction,
          ),
          wire_request_review_threshold: formatCurrencyForPost(
            data.wireRequestReviewThreshold,
          ),
          check_deposit_program_limit_per_rolling_day: formatCurrencyForPost(
            data.checkDepositProgramLimitPerRollingDay,
          ),
          check_deposit_account_limit_per_calendar_day: formatCurrencyForPost(
            data.checkDepositAccountLimitPerCalendarDay,
          ),
          check_deposit_account_limit_per_calendar_month: formatCurrencyForPost(
            data.checkDepositAccountLimitPerCalendarMonth,
          ),
          check_deposit_limit_per_transaction: formatCurrencyForPost(
            data.checkDepositLimitPerTransaction,
          ),
          check_issuance_program_limit_per_rolling_day: formatCurrencyForPost(
            data.checkIssuanceProgramLimitPerRollingDay,
          ),
          check_issuance_account_limit_per_calendar_day: formatCurrencyForPost(
            data.checkIssuanceAccountLimitPerCalendarDay,
          ),
          check_issuance_account_limit_per_calendar_month:
            formatCurrencyForPost(
              data.checkIssuanceAccountLimitPerCalendarMonth,
            ),
          check_issuance_limit_per_transaction: formatCurrencyForPost(
            data.checkIssuanceLimitPerTransaction,
          ),
        },
      });

      queryClient.invalidateQueries({
        queryKey: [API_ROUTES.PROGRAM_SETTINGS],
      });

      return status;
    },
  });

  return {
    isLoadingUpdateProgramSettings,
    updateProgramSettings,
  };
};
